import '.bb{position:relative;}.ssr-plugin'
import '.u{display:inline-flex;}.ssr-plugin'
import '.f{align-items:center;}.ssr-plugin'
import '.al{cursor:pointer;}.ssr-plugin'
import '.ar{position:absolute;}.ssr-plugin'
import '.ie{top:calc(100%);}.ssr-plugin'
import '.if{z-index:20;}.ssr-plugin'
import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react'
import { styled, type Style } from 'inlines'
import Column from '@app/components/layout/Column'
import { HorizontalSeparator } from '@app/components/core/separator/HorizontalSeparator'
import {
  TextLink,
  type TextLinkProps,
} from '@app/components/core/link/TextLink'
import { Button, type ButtonProps } from '@app/components/core/button/Button'
import useMediaQuery from '@app/util/useMediaQuery'

interface DropdownMenuLinkItem {
  type: 'link'
  label: string
  to: string
  id?: string
  linkProps?: Omit<TextLinkProps, 'children' | 'to'>
}

interface DropdownMenuButtonItem {
  type: 'button'
  label: string
  id?: string
  buttonProps?: Omit<ButtonProps, 'label'>
}

export type DropdownMenuItem = DropdownMenuLinkItem | DropdownMenuButtonItem

type DropdownMenuAlignment = 'start' | 'center' | 'end'

interface DropdownMenuProps {
  items: DropdownMenuItem[]
  children: ReactNode
  showOnHoverDesktop?: boolean
  alignment?: DropdownMenuAlignment
  open?: boolean
}

const baseItemStyle: Style = {
  padding: '1rem 1.5rem',
  justifyContent: 'flex-start',
  width: '100%',
  textDecoration: 'none',

  '&:hover': {
    backgroundColor: 'var(--gray-100)',
  },
}

const horizontalAlignmentStyles: Record<DropdownMenuAlignment, Style> = {
  start: { left: 0, right: 'auto' },
  center: { left: '50%', right: 'auto', transform: 'translateX(-50%)' },
  end: { right: 0, left: 'auto' },
}

const defaultTextStyle: Style = {
  fontWeight: 400,
}

export const DropdownMenu = ({
  items,
  children,
  showOnHoverDesktop = false,
  open: externalOpen,
  alignment = 'end',
}: DropdownMenuProps) => {
  const isMobile = useMediaQuery()
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLDivElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return
      }

      setIsOpen(false)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, setIsOpen])

  const handleTriggerKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setIsOpen((previous) => !previous)
    }
  }

  const open = externalOpen ?? isOpen
  const shouldRenderMenu = open && items.length > 0
  const useHover = showOnHoverDesktop && !isMobile
  const triggerClickHandler = useHover
    ? undefined
    : () => setIsOpen((previous) => !previous)
  const horizontalAlignment = isMobile ? 'end' : alignment
  const menuOffsetStyle = horizontalAlignmentStyles[horizontalAlignment]

  return (
    <div className="bb u f"
      onMouseEnter={useHover ? () => setIsOpen(true) : undefined}
      onMouseLeave={useHover ? () => setIsOpen(false) : undefined}
    >
      <div className="al u"
        ref={triggerRef}
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={shouldRenderMenu}
        onClick={triggerClickHandler}
        onKeyDown={handleTriggerKeyDown}
      >
        {children}
      </div>
      {shouldRenderMenu ? (
        <styled.div className="ar ie if"
          ref={menuRef}
          style={{
            ...menuOffsetStyle,
          }}
        >
          <Column
            gap={0}
            onClick={() => setIsOpen(false)}
            style={{
              marginTop: '1rem',
              borderRadius: '0.625rem',
              backgroundColor: 'var(--shades-white)',
              border: '1px solid var(--gray-100)',
              boxShadow: '0 3px 10px 0 rgba(0, 0, 0, 0.05)',
              alignItems: 'stretch',
              overflow: 'hidden',
            }}
          >
            {items.map((item, index) => (
              <Fragment key={item.id ?? `${item.type}-${index}`}>
                {item.type === 'link'
                  ? renderLinkItem(item)
                  : renderButtonItem(item)}

                {index < items.length - 1 && (
                  <HorizontalSeparator color="var(--gray-100)" />
                )}
              </Fragment>
            ))}
          </Column>
        </styled.div>
      ) : null}
    </div>
  )
}

const renderLinkItem = (item: DropdownMenuLinkItem) => {
  const { label, to, linkProps } = item
  const { containerStyle, textStyle, ...rest } = linkProps ?? {}

  return (
    <TextLink
      to={to}
      {...rest}
      containerStyle={{ ...baseItemStyle, ...containerStyle }}
      textStyle={{ ...defaultTextStyle, ...textStyle }}
    >
      {label}
    </TextLink>
  )
}

const renderButtonItem = (item: DropdownMenuButtonItem) => {
  const { label, buttonProps } = item
  const { style, textStyle, variant, ...rest } = buttonProps ?? {}

  return (
    <Button
      label={label}
      variant={variant ?? 'link'}
      {...rest}
      style={{ ...baseItemStyle, ...style }}
      textStyle={{ ...defaultTextStyle, ...textStyle }}
    />
  )
}
